import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/shared/components/shadcn/Button";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { useRevokeAppointmentLink } from "../hooks/appointmentManagementQueries";

interface AppointmentLinkRevocationProps {
  appointmentId: string;
  revision: number;
  disabled: boolean;
  onPendingChange: (pending: boolean) => void;
  onRefresh: () => void;
}

export function AppointmentLinkRevocation({
  appointmentId, revision, disabled, onPendingChange, onRefresh,
}: AppointmentLinkRevocationProps) {
  const [open, setOpen] = useState(false);
  const [expectedRevision, setExpectedRevision] = useState(revision);
  const [notice, setNotice] = useState("");
  const descriptionId = useId();
  const submitting = useRef(false);
  const mutation = useRevokeAppointmentLink(appointmentId);

  useEffect(() => {
    onPendingChange(mutation.isPending);
    return () => onPendingChange(false);
  }, [mutation.isPending, onPendingChange]);

  function chooseRevoke() {
    setNotice("");
    mutation.reset();
    setExpectedRevision(revision);
    setOpen(true);
  }

  async function confirm() {
    if (!open || submitting.current) return;
    submitting.current = true;
    try {
      await mutation.mutateAsync({ expectedRevision });
      setNotice("Customer link revoked. The appointment has not been cancelled.");
    } catch {
      // Require a new confirmation after the hook refreshes and exposes the error.
    } finally {
      setOpen(false);
      submitting.current = false;
    }
  }

  return (
    <div className="space-y-2">
      <Button variant="outline" size="sm" aria-describedby={descriptionId}
        disabled={disabled || mutation.isPending} onClick={chooseRevoke}>Revoke customer link</Button>
      <p id={descriptionId} className="text-xs text-muted-foreground">Disable the customer's management link without cancelling the appointment.</p>
      {mutation.error && <ErrorState title="Couldn't confirm link revocation" message={mutation.error.message} onRetry={onRefresh} />}
      <p role="status" aria-live="polite" className="text-sm text-primary empty:hidden">{notice}</p>
      <ConfirmationDialog open={open} onOpenChange={setOpen}
        title="Revoke customer link?"
        description="The customer's management link will stop working. This does not cancel the appointment."
        confirmLabel="Revoke link" dismissLabel="Keep link"
        destructive pending={mutation.isPending} onConfirm={() => void confirm()} />
    </div>
  );
}
